import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { STATUT_FACTURE, MODE_REGLEMENT } from "../utils/constantes";
import './Factures.css';

export default function Factures() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
     const [taxes, setTaxes] = useState({
            tva_taux: 18,
            css_taux: 1,
            tva_active: true,
            css_active: true
        });
        
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchFactures();
        fetchStats();
         fetchTaxes();

     
    }, [filtreStatut]);
  const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=taxes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };
    const fetchFactures = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/factures.php';
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFactures(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const config = STATUT_FACTURE[statut] || STATUT_FACTURE.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2 statut-badge"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Filtrage et tri des données
    const filteredFactures = useMemo(() => {
        return factures
            .filter(f => 
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête avec animation */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon">
                                        <i className="ri-file-list-3-line text-primary"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Gestion des factures</h1>
                                        <p className="page-subtitle mb-0">
                                            Gérez vos factures, suivez les paiements et les échéances
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/factures/nouveau')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-add-line me-2"></i>
                                    Nouvelle facture
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques améliorées */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="stat-icon-wrapper bg-primary">
                                                <i className="ri-file-list-3-line text-white"></i>
                                            </div>
                                            <div className="stat-content" style={{marginLeft:'10px'}}>
                                                <span className="stat-label">Factures (mois)</span>
                                                <h3 className="stat-value">{stats.mois?.nb_factures || 0}</h3>
                                                <small className="stat-trend text-primary">
                                                    <i className="ri-arrow-up-line"></i>
                                                    {formatCurrency(stats.mois?.total_ca || 0)}
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="stat-icon-wrapper bg-success">
                                                <i className="ri-check-double-line text-white"></i>
                                            </div>
                                            <div className="stat-content" style={{marginLeft:'10px'}}>
                                                <span className="stat-label">Encaissé</span>
                                                <h4 className="stat-value">{formatCurrency(stats.mois?.total_regle || 0)}</h4>
                                                <small className="stat-trend text-success">
                                                    <i className="ri-check-line"></i>  
                                                    Réglé
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="stat-icon-wrapper bg-warning">
                                                <i className="ri-time-line text-white"></i>
                                            </div>
                                            <div className="stat-content" style={{marginLeft:'10px'}}>
                                                <span className="stat-label">Impayés</span>
                                                <h3 className="stat-value">{stats.impayes?.nb || 0}</h3>
                                                <small className="stat-trend text-warning">
                                                    <i className="ri-alert-line"></i>
                                                    {formatCurrency(stats.impayes?.total || 0)}
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0">
                                    <Card.Body>
                                        <div className="d-flex align-items-center">
                                            <div className="stat-icon-wrapper bg-info">
                                                <i className="ri-bar-chart-2-line text-white"></i>
                                            </div>
                                            <div className="stat-content" style={{marginLeft:'10px'}}>
                                                <span className="stat-label">TVA</span>
                                                <h3 className="stat-value">{formatCurrency(stats.mois?.total_ca * 0.18 || 0)}</h3>
                                                <small className="stat-trend text-info">
                                                    <i className="ri-percent-line"></i>
                                                  
                                                    {taxes.tva_active ? taxes.tva_taux : 0}%
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres améliorés */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher facture, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="tous">📋 Tous les statuts</option>
                                        <option value="brouillon">📝 Brouillons</option>
                                        <option value="emise">📨 Émises</option>
                                        <option value="payee_partielle">⏳ Paiements partiels</option>
                                        <option value="payee">✅ Payées</option>
                                        <option value="impayee">⚠️ Impayées</option>
                                    </Form.Select>
                                </Col>
                                <Col md={5} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredFactures.length}</span>
                                        <span className="text-muted ms-2">facture(s) trouvée(s)</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                            
                            {showFilters && (
                                <Row className="mt-3">
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Trier par date</option>
                                            <option>Plus récentes</option>
                                            <option>Plus anciennes</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Filtrer par client</option>
                                            <option>Clients actifs</option>
                                            <option>Nouveaux clients</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Liste des factures avec pagination */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Facture</th>
                                            <th>Client</th>
                                            <SortableHeader field="montant">Montant TTC</SortableHeader>
                                            <th className="text-end">Réglé</th>
                                            <th className="text-end">Reste</th>
                                            <th>Échéance</th>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="loading-container">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-3 text-muted">Chargement des factures...</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((facture) => {
                                                const reste = facture.net_a_payer - (facture.montant_regle || 0);
                                                const joursRetard = getJoursRetard(facture.date_echeance);
                                                const estEnRetard = joursRetard > 0 && facture.statut !== 'payee';
                                                
                                                return (
                                                    <tr 
                                                        key={facture.id_facture} 
                                                        className={`facture-row ${estEnRetard ? 'en-retard' : ''}`}
                                                    >
                                                        <td>
                                                            <Link 
                                                                to={`/ventes/facturesDetails/${facture.id_facture}`} 
                                                                className="facture-link"
                                                                title={facture.numero_facture}
                                                            >
                                                                <span className="facture-num"> 
                                                                    {facture.numero_facture?.length > 5 
                                                                        ? facture.numero_facture.substring(0, 5) + '...' 
                                                                        : facture.numero_facture}
                                                                </span>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                <span className="client-name">{facture.client_nom}</span>
                                                                <small className="client-code">{facture.code_client}</small>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="montant-ttc fw-bold">{formatCurrency(facture.net_a_payer)}</span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="montant-regle text-success">{formatCurrency(facture.montant_regle || 0)}</span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className={`montant-reste ${reste > 0 ? 'text-danger' : 'text-success'}`}>
                                                                {formatCurrency(reste)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="echeance-info">
                                                                <span className={`echeance-date ${estEnRetard ? 'text-danger fw-bold' : ''}`}>
                                                                    {formatDate(facture.date_echeance)}
                                                                </span>
                                                                {estEnRetard && (
                                                                    <Badge bg="danger" className="ms-2 retard-badge">
                                                                        J+{joursRetard}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{getStatutBadge(facture.statut)}</td>
                                                          <td className="text-center" style={{ minWidth: '160px' }}>
    <div className="actions-container d-flex justify-content-center align-items-center gap-2">
        <ActionButton
            variant="outline-info"
            icon="ri-eye-line"
            onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
            tooltip="Voir détails"
        />
        {facture.statut !== 'payee' && facture.statut !== 'annulee' && (
            <ActionButton
                variant="outline-success"
                icon="ri-bank-card-line"
                onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                tooltip="Encaisser"
            />
        )}
        <ActionButton
            variant="outline-warning"
            icon="ri-file-copy-line"
            onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
            tooltip="Créer un avoir"
        />
    </div>
</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-file-list-3-line empty-icon"></i>
                                                        <h5>Aucune facture trouvée</h5>
                                                        <p className="text-muted">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre première facture"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/ventes/factures/nouveau')}
                                                            >
                                                                <i className="ri-add-line me-2"></i>
                                                                Créer une facture
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredFactures.length)} sur {filteredFactures.length} facture(s)
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                /* Styles pour la pagination */
                .pagination {
                    gap: 5px;
                }
                
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                }
                
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Styles existants conservés */
                .fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .header-section {
                    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
                    padding: 1.5rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .page-icon {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #4361ee20, #80b3ff20);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                }

                .page-title {
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    font-size: 0.95rem;
                }

                .btn-gradient {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border: none;
                    color: white;
                    transition: all 0.3s ease;
                }

                .btn-gradient:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px #4361ee40;
                }

                .stats-row {
                    margin-top: 1.5rem;
                }

                .stat-card {
                    border-radius: 20px !important;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    background: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
                }

                .stat-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                }

                .stat-icon-wrapper.bg-primary { background: linear-gradient(135deg, #4361ee, #3a56d4); }
                .stat-icon-wrapper.bg-success { background: linear-gradient(135deg, #10b981, #059669); }
                .stat-icon-wrapper.bg-warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
                .stat-icon-wrapper.bg-info { background: linear-gradient(135deg, #3b82f6, #2563eb); }

                .stat-content {
                    flex: 1;
                }

                .stat-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b;
                    text-transform: uppercase;
                }

                .stat-value {
                    font-size: 0.9rem;
                    font-weight: 700;
                    line-height: 1.2;
                }

                .stat-trend {
                    font-size: 0.8rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .filter-card {
                    border-radius: 16px !important;
                    background: white;
                }

                .search-input .input-group-text {
                    background: white;
                    border-right: none;
                    color: #64748b;
                }

                .search-input .form-control {
                    border-left: none;
                    padding: 0.6rem 0.75rem;
                }

                .filter-select {
                    border-radius: 12px;
                    padding: 0.6rem 1rem;
                    border: 2px solid #e2e8f0;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .filter-select:focus {
                    border-color: #4361ee;
                    box-shadow: 0 0 0 3px #4361ee20;
                }

                .badge-count {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .table-card {
                    border-radius: 16px !important;
                    overflow: hidden;
                    background: white;
                }

                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .sortable-header {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .sortable-header:hover {
                    background: #f1f5f9;
                }

                .custom-table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #e2e8f0;
                }

                .facture-row {
                    transition: all 0.2s ease;
                }

                .facture-row:hover {
                    background: #f8fafc !important;
                    transform: scale(1.01);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }

                .facture-row.en-retard {
                    background: #fff5f5;
                }

                .facture-link {
                    text-decoration: none;
                    font-weight: 600;
                    color: #1e293b;
                    position: relative;
                }

                .facture-link:hover {
                    color: #4361ee;
                }

                .facture-link::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 0;
                    height: 2px;
                    background: #4361ee;
                    transition: width 0.3s ease;
                }

                .facture-link:hover::after {
                    width: 100%;
                }

                .client-info {
                    display: flex;
                    flex-direction: column;
                }

                .client-name {
                    font-weight: 500;
                    color: #1e293b;
                }

                .client-code {
                    color: #64748b;
                    font-size: 0.8rem;
                }

                .echeance-info {
                    display: flex;
                    align-items: center;
                }

                .retard-badge {
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }

                .actions-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }

                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                .action-btn.btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }

                .action-btn.btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }

                .action-btn.btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }

                .empty-state {
                    padding: 3rem;
                    text-align: center;
                }

                .empty-icon {
                    font-size: 4rem;
                    color: #cbd5e1;
                    margin-bottom: 1.5rem;
                    animation: bounce 2s infinite;
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }


                    

                .loading-container {
                    padding: 3rem;
                    text-align: center;
                }

                .loading-container .spinner-border {
                    width: 3rem;
                    height: 3rem;
                }

                @media (max-width: 768px) {
                    .page-title {
                        font-size: 1.4rem;
                    }
                    
                    .page-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 1.5rem;
                    }
                    
                    .stat-value {
                        font-size: 1rem;
                    }
                    
                    .stat-icon-wrapper {
                        width: 50px;
                        height: 50px;
                    }
                    
                    .stat-icon-wrapper i {
                        font-size: 1.5rem !important;
                    }
                    
                    .actions-container {
                        gap: 4px;
                    }
                    
                    .action-btn {
                        width: 32px;
                        height: 32px;
                    }
                    
                    .pagination .page-link {
                        padding: 0.4rem 0.8rem;
                        font-size: 0.9rem;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}